import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePostVehicle from '../../../../API/hooks/vehicles/usePostVehicle';
import { PostVehicleRequest } from '../../../../API/types/Vehicles';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import S from './AddDeviceStyles';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';
import {
  generatePreviewLicenseNumber,
  generatePreviewModel,
} from '../helpers';

type Props = {
  fleetId: string;
  toggleModal: (closed: boolean) => void;
};

function AddDevice({ fleetId, toggleModal } : Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePostVehicle(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      licensePlateNumber: '',
      manufacturer: '',
      model: '',
      color: '',
    },
    onSubmit: (values) => mutate({ ...values, fleetId } as unknown as PostVehicleRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.licensePlateNumber.length) errors.firstName = 'Required';
      if (!values.manufacturer.length) errors.lastName = 'Required';
      if (!values.model.length) errors.email = 'Required';
      if (!values.color.length) errors.phoneNumber = 'Required';

      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <h4 className="form-title"> Vehicle details </h4>
          <S.RowContainer>
            <Input
              label="License number"
              name="licensePlateNumber"
              placeholder="AA 12 B 1234"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.licensePlateNumber}
              error={formik.touched.licensePlateNumber && formik.errors.licensePlateNumber}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Manufacturer"
              name="manufacturer"
              placeholder="Manufacturer"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.manufacturer}
              error={formik.touched.manufacturer && formik.errors.manufacturer}
            />
            <Input
              label="Model"
              name="model"
              placeholder="Model"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.model}
              error={formik.touched.model && formik.errors.model}
            />
            <Input
              label="Color"
              name="color"
              placeholder="Color"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.color}
              error={formik.touched.color && formik.errors.color}
            />
          </S.RowContainer>
          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
        <div className="preview-container">
          <div className="preview-card">
            <h4 className="preview-card-title"> Vehicle preview </h4>
            <div className="preview-card-tags-container">
              <img src={vehicleThumbnail} alt="vehicle" />
              <p className="license">
                {generatePreviewLicenseNumber(formik.values.licensePlateNumber)}
              </p>
              <p className="model">
                {generatePreviewModel(formik.values.model)}
              </p>
            </div>
            <div className="preview-card-buttons-container">
              <Button
                height="50px"
                onClick={() => toggleModal(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="add-button"
                height="50px"
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !!submitError}
              >
                Add vehicle
              </Button>
            </div>
          </div>
        </div>
      </form>
    </S.Wrapper>
  );
}

export default AddDevice;
